import {
  Animated,
  type LayoutChangeEvent,
  Platform,
  View,
  useWindowDimensions,
} from "@-/com.npmjs.react-native";
import { TabBar } from "@-/com.npmjs.react-native-tab-view";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import {
  HorizontalScrollEdgeFadeMask,
  useHorizontalScrollEdges,
} from "@-/dev.improve.haniverse.components.ui.scroll-edge-fade";
import {
  SpaceBackdrop,
  SpaceSkyForegroundScope,
  useIsSpaceBackgroundEnabled,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeElementContainer,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { TOOLBAR_MATERIAL_TABS_HEIGHT } from "#toolbar-material-tabs-height";

// `TabBar` reads its tab width and indicator size by flattening these styles, and sets inline
// default colors (#2196f3 bar, #ffeb3b indicator). On web, `withUniwind` turns classNames into CSS
// class references, which `StyleSheet.flatten` can't read and inline defaults override, so the
// strip rendered blue with 40%-wide tabs. Pass real style objects instead.
const tabBarStyle = {
  backgroundColor: "transparent",
  elevation: 0,
  ...Platform.select({ default: { shadowOpacity: 0 }, web: { boxShadow: "none" } }),
};
const tabStyle = {
  height: TOOLBAR_MATERIAL_TABS_HEIGHT,
  minWidth: 80,
  paddingHorizontal: 16,
  width: "auto",
} as const;
// Without a width, a non-scrolling `TabBar` gives every tab an equal share of its layout width.
const fillTabStyle = { height: TOOLBAR_MATERIAL_TABS_HEIGHT, paddingHorizontal: 8 } as const;
// `TabBar` only knows equal shares or measured widths, so a filled strip with an icon tab measures
// its tabs: each label sets its own width (the icon tab narrow, the rest an equal share of the
// remainder) and the indicator follows the measured widths.
const measuredFillTabStyle = {
  height: TOOLBAR_MATERIAL_TABS_HEIGHT,
  paddingHorizontal: 0,
  width: "auto",
} as const;
/** An icon tab's width in a filled strip: the icon and its touch padding. */
const FILL_ICON_TAB_WIDTH = 48;
const CSS_COLOR_PATTERN = /^(#|rgb|hsl)/;

/**
 * Hangs `accessory` beneath the focused label, outside its layout so the label never moves.
 * `TabBarItem` draws every tab's focused label over its unfocused one and fades it with the
 * selection, so the accessory crossfades from tab to tab with the label's color.
 */
function withAccessory(
  accessory: ReactNode,
  renderLabel: (props: { focused: boolean }) => ReactNode,
): (props: { focused: boolean }) => ReactNode {
  if (accessory === undefined || accessory === null) {
    return renderLabel;
  }
  return ({ focused }) =>
    focused ? (
      <View>
        {renderLabel({ focused })}
        <View className="absolute inset-x-0 top-full items-center">{accessory}</View>
      </View>
    ) : (
      renderLabel({ focused })
    );
}

/** Material top tabs without a nested pager: the owner keeps its existing list and route state. */
export function ToolbarMaterialTabs<Value extends string | number>({
  activeTabAccessory,
  className,
  fill = false,
  floating = false,
  onActiveTabPress,
  onValueChange,
  options,
  value,
  variant = "toolbar",
}: {
  /**
   * Hangs beneath the selected tab's label without moving it (a grabber for a sheet waiting
   * behind the strip, say), and crossfades to the next tab with the label's selected color.
   */
  activeTabAccessory?: ReactNode;
  className?: string;
  /**
   * Every tab takes an equal share of the strip, as if each were `flex-1`, instead of sizing to
   * its label; the strip then never scrolls. Use only when every label fits its share.
   */
  fill?: boolean;
  /**
   * The strip floats with a `floating` top bar over the page scroller. On iOS 26 it then has no
   * surface or rule of its own: UIKit blurs the content passing beneath it, as beneath the bar.
   * Leave false (default) when tab labels must stay fully opaque — scrolling content under glass
   * washes out labels like "HSK 1". Pages with material tabs should pin an opaque strip instead.
   */
  floating?: boolean;
  /** Pressing the tab that is already selected, which changes nothing else. */
  onActiveTabPress?: () => void;
  onValueChange: (value: Value) => void;
  /**
   * `sublabel` stays on a smaller second line on every platform; `disabled` ignores presses. `icon`
   * replaces the visible label (which still names the tab for accessibility); with `fill`, an
   * icon tab stays narrow and the labelled tabs share the rest.
   */
  options: readonly {
    disabled?: boolean;
    icon?: IconName;
    label: string;
    sublabel?: string;
    value: Value;
  }[];
  value: Value;
  /** Use `surface-header` inside `SurfaceHeader` to match its backdrop and edge fades. */
  variant?: "toolbar" | "surface-header";
}) {
  const { width } = useWindowDimensions();
  const primaryValue = String(useCSSVariable("--color-primary") ?? "0 0% 9%");
  // Over the experimental space background the strip shows the sky, so it takes light ink.
  const isSpaceBackgroundEnabled = useIsSpaceBackgroundEnabled() && variant === "toolbar";
  const indicatorStyle = useMemo(
    () => ({
      backgroundColor: isSpaceBackgroundEnabled
        ? "#ffffff"
        : CSS_COLOR_PATTERN.test(primaryValue)
          ? primaryValue
          : `hsl(${primaryValue})`,
      height: 2,
    }),
    [isSpaceBackgroundEnabled, primaryValue],
  );
  const [layout, setLayout] = useState({ height: 48, width });
  // The strip scrolls past the screen edges; fade only the sides that still have tabs beyond them.
  const edges = useHorizontalScrollEdges();
  const onLayout = (event: LayoutChangeEvent) => {
    setLayout(event.nativeEvent.layout);
    edges.onLayout(event);
  };
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [animatedIndex] = useState(() => new Animated.Value(index));
  const position = useMemo(
    () => animatedIndex.interpolate({ inputRange: [0, 1], outputRange: [0, 1] }),
    [animatedIndex],
  );
  useEffect(() => {
    const animation = Animated.timing(animatedIndex, {
      duration: 180,
      toValue: index,
      useNativeDriver: Platform.OS !== "web",
    });
    animation.start();
    return () => animation.stop();
  }, [animatedIndex, index]);
  const routes = useMemo(
    () =>
      options.map((option) => ({
        disabled: option.disabled === true,
        icon: option.icon,
        key: String(option.value),
        sublabel: option.sublabel,
        title: option.label,
      })),
    [options],
  );
  const iconTabCount = routes.filter((route) => route.icon !== undefined).length;
  const measuresFillTabs = fill && iconTabCount > 0;
  const labelledFillTabWidth = Math.max(
    0,
    (layout.width - iconTabCount * FILL_ICON_TAB_WIDTH) / Math.max(1, routes.length - iconTabCount),
  );
  const descriptors = useMemo(
    () =>
      Object.fromEntries(
        routes.map((route) => [
          route.key,
          {
            // `TabBarItem` exposes no disabled state, so the label carries it for screen readers.
            accessibilityLabel: `Select ${route.title.replaceAll("\n", " ")}${route.disabled ? " (unavailable)" : ""}`,
            // TabBarItem overlays the focused label on a box sized by the unfocused one, so both
            // states share one font weight; a bolder focused label wraps ("HSK / 1").
            label: withAccessory(activeTabAccessory, ({ focused }: { focused: boolean }) => {
              const textColorClassName = route.disabled
                ? "text-muted-foreground opacity-50"
                : focused
                  ? "text-foreground"
                  : "text-muted-foreground";
              const labelClassName = `text-sm font-medium ${textColorClassName}`;
              // Explicit sublabels are secondary counts; multiline titles retain 14pt type.
              const sublabelClassName =
                route.sublabel === undefined
                  ? labelClassName
                  : `text-xs font-medium ${textColorClassName}`;
              if (route.icon !== undefined) {
                const icon = (
                  <Icon
                    className={
                      route.disabled
                        ? "text-muted-foreground opacity-50"
                        : focused
                          ? "text-foreground"
                          : "text-muted-foreground"
                    }
                    name={route.icon}
                    size={20}
                  />
                );
                return measuresFillTabs ? (
                  <View className="items-center" style={{ width: FILL_ICON_TAB_WIDTH }}>
                    {icon}
                  </View>
                ) : (
                  icon
                );
              }
              if (measuresFillTabs) {
                const [title, multilineSubtitle] = route.title.split("\n");
                const subtitle = route.sublabel ?? multilineSubtitle;
                return (
                  <View className="items-center" style={{ width: labelledFillTabWidth }}>
                    <Text className={labelClassName}>{title}</Text>
                    {subtitle === undefined ? null : (
                      <Text className={sublabelClassName}>{subtitle}</Text>
                    )}
                  </View>
                );
              }
              // Web joins multiline titles onto one line; explicit sublabels stay stacked.
              if (Platform.OS === "web" && route.sublabel === undefined) {
                return <Text className={labelClassName}>{route.title.replaceAll("\n", " ")}</Text>;
              }
              const [title, multilineSubtitle] = route.title.split("\n");
              const subtitle = route.sublabel ?? multilineSubtitle;
              return (
                <View className="items-center">
                  <Text className={labelClassName}>{title}</Text>
                  {subtitle === undefined ? null : (
                    <Text className={sublabelClassName}>{subtitle}</Text>
                  )}
                </View>
              );
            }),
          },
        ]),
      ),
    [activeTabAccessory, labelledFillTabWidth, measuresFillTabs, routes],
  );
  const tabBar = (
    <TabBar
      indicatorStyle={indicatorStyle}
      jumpTo={(key) => {
        const option = options.find((item) => String(item.value) === key);
        if (option !== undefined && option.disabled !== true) {
          onValueChange(option.value);
        }
      }}
      layout={layout}
      navigationState={{ index, routes }}
      onContentSizeChange={edges.onContentSizeChange}
      onScroll={edges.onScroll}
      onTabPress={({ route }) => {
        if (route.key === String(value)) {
          onActiveTabPress?.();
        }
      }}
      options={descriptors}
      position={position}
      scrollEnabled={!fill}
      // The wrapper supplies the backdrop; the tab bar stays transparent.
      style={tabBarStyle}
      tabStyle={measuresFillTabs ? measuredFillTabStyle : fill ? fillTabStyle : tabStyle}
    />
  );
  if (variant === "toolbar" && floating && isScrollEdgeEffectSupported) {
    return (
      <ScrollEdgeElementContainer
        className={className}
        // The labels are text, which does not shape the edge effect by itself.
        coversBounds
        onLayout={onLayout}
      >
        {/* A mask, not a fade into a strip colour: the page passes beneath this strip. */}
        <HorizontalScrollEdgeFadeMask edges={edges}>{tabBar}</HorizontalScrollEdgeFadeMask>
      </ScrollEdgeElementContainer>
    );
  }
  return (
    <View
      // Over the sky the strip and the list beneath share one backdrop, so no separator line.
      className={`${variant === "surface-header" ? "bg-surface-header" : `bg-muted ${isSpaceBackgroundEnabled ? "" : "border-b border-border"}`} ${className ?? ""}`}
      onLayout={onLayout}
    >
      {variant === "toolbar" ? <SpaceBackdrop /> : null}
      <HorizontalScrollEdgeFadeMask edges={edges}>
        <SpaceSkyForegroundScope enabled={isSpaceBackgroundEnabled}>
          {tabBar}
        </SpaceSkyForegroundScope>
      </HorizontalScrollEdgeFadeMask>
    </View>
  );
}
